import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';

const API_BASE = process.env.REACT_APP_API_URL || '';
const ACCEPT = '.pdf,.docx,.txt,.md,.csv';
const POLL_MS = 2500;

function formatBytes(n) {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(x) {
  return x ? new Date(x).toLocaleString('de-DE') : '';
}

function statusBadge(doc) {
  if (doc.status === 'ready') return { label: '✓ bereit', color: '#5af5c8' };
  if (doc.status === 'failed') return { label: '✕ Fehler', color: '#f55a8a' };
  if (doc.status === 'awaiting_approval') return { label: '⏳ wartet auf Freigabe', color: '#f5a85a' };
  if (doc.status === 'rejected') return { label: '✕ abgelehnt', color: '#f55a8a' };
  if (doc.status === 'pending') {
    if (doc.totalChunks && doc.chunkCount) {
      const pct = Math.floor((doc.chunkCount / doc.totalChunks) * 100);
      return { label: `… ${pct}% (${doc.chunkCount}/${doc.totalChunks})`, color: '#f5d55a' };
    }
    return { label: '… verarbeitet', color: '#f5d55a' };
  }
  return { label: doc.status, color: '#888' };
}

function accessSummary(doc) {
  if ((doc.accessMode || 'all') === 'all') return { label: '🌐 Alle', color: '#7aa2f7' };
  const nDept = (doc.accessDepartmentIds || []).length;
  const nUser = (doc.accessUserIds || []).length;
  if (nDept === 0 && nUser === 0) return { label: '🔒 Nur Admins', color: '#f5d55a' };
  const parts = [];
  if (nDept) parts.push(`${nDept} Abt.`);
  if (nUser) parts.push(`${nUser} Pers.`);
  return { label: '🔒 ' + parts.join(' · '), color: '#c8a2f7' };
}

// Wiederverwendbarer Editor für die Zugriffsauswahl (Upload + Bearbeiten).
function AccessEditor({ mode, deptIds, userIds, departments, members, onChange }) {
  const toggle = (list, id) => (list.includes(id) ? list.filter(x => x !== id) : [...list, id]);
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', gap: 16, marginBottom: 8, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: '#ccc' }}>
          <input type="radio" checked={mode === 'all'} onChange={() => onChange({ mode: 'all', deptIds, userIds })} />
          🌐 Für alle Mitglieder
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: '#ccc' }}>
          <input type="radio" checked={mode === 'restricted'} onChange={() => onChange({ mode: 'restricted', deptIds, userIds })} />
          🔒 Eingeschränkt
        </label>
      </div>
      {mode === 'restricted' && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Abteilungen</div>
            {departments.length === 0 ? (
              <div style={{ fontSize: 12, color: '#666' }}>Keine Abteilungen angelegt.</div>
            ) : (
              <div style={{ maxHeight: 140, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {departments.map(d => (
                  <label key={d._id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#ccc', cursor: 'pointer' }}>
                    <input type="checkbox" checked={deptIds.includes(d._id)} onChange={() => onChange({ mode, deptIds: toggle(deptIds, d._id), userIds })} />
                    {d.parentId ? '↳ ' : ''}{d.name}
                  </label>
                ))}
              </div>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Einzelne Personen</div>
            {members.length === 0 ? (
              <div style={{ fontSize: 12, color: '#666' }}>Keine Mitglieder.</div>
            ) : (
              <div style={{ maxHeight: 140, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {members.map(m => (
                  <label key={m.userId} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#ccc', cursor: 'pointer' }}>
                    <input type="checkbox" checked={userIds.includes(m.userId)} onChange={() => onChange({ mode, deptIds, userIds: toggle(userIds, m.userId) })} />
                    {m.username || m.email}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Audit-Zeilen: wer hat hochgeladen / freigegeben / verarbeitet / abgelehnt.
function AuditLines({ doc }) {
  const line = { fontSize: 12, color: '#777', marginTop: 2 };
  return (
    <>
      <div style={line}>
        ⬆ hochgeladen von <strong style={{ color: '#aaa' }}>{doc.createdByName || '—'}</strong> · {fmtDate(doc.createdAt)}
      </div>
      {doc.approvedAt && (
        <div style={line}>
          ✓ freigegeben von <strong style={{ color: '#aaa' }}>{doc.approvedByName || '—'}</strong>
          {doc.approvalMode === 'auto' ? ' (automatisch)' : ''} · {fmtDate(doc.approvedAt)}
        </div>
      )}
      {doc.indexedAt && (
        <div style={line}>⚙ verarbeitet · {fmtDate(doc.indexedAt)}</div>
      )}
      {doc.rejectedAt && (
        <div style={{ ...line, color: '#f55a8a' }}>
          ✕ abgelehnt von <strong>{doc.rejectedByName || '—'}</strong> · {fmtDate(doc.rejectedAt)}
          {doc.rejectReason ? ` — „${doc.rejectReason}"` : ''}
        </div>
      )}
    </>
  );
}

export default function Knowledge({ onBack }) {
  const { token, user } = useAuth();
  const { activeTenant } = useTenant();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const [departments, setDepartments] = useState([]);
  const [members, setMembers] = useState([]);
  const [uploadAccess, setUploadAccess] = useState({ mode: 'all', deptIds: [], userIds: [] });
  const [uploadHidden, setUploadHidden] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingAccess, setSavingAccess] = useState(false);

  const isAdmin = activeTenant && (activeTenant.role === 'admin' || activeTenant.role === 'owner');
  // Server-Admin (globale Rolle) — nur er darf Dokumente verstecken/sehen.
  const isServerAdmin = user?.role === 'admin';

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'x-tenant-id': activeTenant._id,
  }), [token, activeTenant]);

  const load = useCallback(async () => {
    if (!activeTenant) return;
    try {
      const r = await fetch(`${API_BASE}/api/documents`, { headers: authHeaders() });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Konnte Dokumente nicht laden.');
      setDocs(data.documents || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [activeTenant, authHeaders]);

  useEffect(() => { load(); }, [load]);

  // Abteilungen + Mitglieder für die Zugriffsauswahl (für alle Mitglieder lesbar).
  useEffect(() => {
    if (!activeTenant) return;
    (async () => {
      try {
        const [dRes, mRes] = await Promise.all([
          fetch(`${API_BASE}/api/departments`, { headers: authHeaders() }),
          fetch(`${API_BASE}/api/tenants/${activeTenant._id}/members`, { headers: authHeaders() }),
        ]);
        const dData = await dRes.json();
        const mData = await mRes.json();
        if (dRes.ok) setDepartments(dData.departments || []);
        if (mRes.ok) setMembers(mData.members || []);
      } catch (e) { /* nicht kritisch */ }
    })();
  }, [activeTenant, authHeaders]);

  // Polling, solange ein Dokument verarbeitet wird.
  useEffect(() => {
    const hasPending = docs.some(d => d.status === 'pending');
    if (hasPending && !pollRef.current) pollRef.current = setInterval(load, POLL_MS);
    if (!hasPending && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [docs, load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('accessMode', uploadAccess.mode);
      fd.append('accessDepartmentIds', JSON.stringify(uploadAccess.deptIds));
      fd.append('accessUserIds', JSON.stringify(uploadAccess.userIds));
      if (isServerAdmin && uploadHidden) fd.append('hidden', 'true');
      const r = await fetch(`${API_BASE}/api/documents`, { method: 'POST', headers: authHeaders(), body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Upload fehlgeschlagen.');
      setDocs(prev => [data.document, ...prev]);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Dokument inklusive Embeddings löschen?')) return;
    try {
      const r = await fetch(`${API_BASE}/api/documents/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || 'Löschen fehlgeschlagen.'); }
      setDocs(prev => prev.filter(d => d._id !== id));
    } catch (e) { setError(e.message); }
  };

  const handleApprove = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/api/documents/${id}/approve`, { method: 'POST', headers: authHeaders() });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Freigabe fehlgeschlagen.');
      setDocs(prev => prev.map(d => d._id === id ? data.document : d));
    } catch (e) { setError(e.message); }
  };

  const handleRetry = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/api/documents/${id}/retry`, { method: 'POST', headers: authHeaders() });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Erneuter Versuch fehlgeschlagen.');
      setDocs(prev => prev.map(d => d._id === id ? data.document : d));
    } catch (e) { setError(e.message); }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Grund der Ablehnung (optional):', '');
    if (reason === null) return; // abgebrochen
    try {
      const r = await fetch(`${API_BASE}/api/documents/${id}/reject`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Ablehnen fehlgeschlagen.');
      setDocs(prev => prev.map(d => d._id === id ? data.document : d));
    } catch (e) { setError(e.message); }
  };

  // Datei mit Auth holen und in neuem Tab öffnen (Browser-Vorschau).
  const handleViewFile = async (doc) => {
    try {
      const r = await fetch(`${API_BASE}/api/documents/${doc._id}/file`, { headers: authHeaders() });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || 'Datei konnte nicht geladen werden.'); }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Object-URL nach kurzer Zeit freigeben (Tab hat sie dann geladen).
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setError(e.message); }
  };

  // Versteckt-Status umschalten (nur Server-Admin).
  const handleToggleHidden = async (doc) => {
    try {
      const r = await fetch(`${API_BASE}/api/documents/${doc._id}/hidden`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ hidden: !doc.hidden }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Aktion fehlgeschlagen.');
      setDocs(prev => prev.map(d => d._id === doc._id ? data.document : d));
    } catch (e) { setError(e.message); }
  };

  const startEditing = (doc) => setEditing({
    docId: doc._id, mode: doc.accessMode || 'all',
    deptIds: doc.accessDepartmentIds || [], userIds: doc.accessUserIds || [],
  });

  const saveAccess = async () => {
    if (!editing) return;
    setSavingAccess(true);
    setError('');
    try {
      const r = await fetch(`${API_BASE}/api/documents/${editing.docId}/access`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessMode: editing.mode, accessDepartmentIds: editing.deptIds, accessUserIds: editing.userIds }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Speichern fehlgeschlagen.');
      setDocs(prev => prev.map(d => d._id === editing.docId ? data.document : d));
      setEditing(null);
    } catch (e) { setError(e.message); } finally { setSavingAccess(false); }
  };

  if (!activeTenant) {
    return (
      <div className="page-container">
        <div className="page-header">
          <button className="icon-btn" onClick={onBack}>←</button>
          <h2>📚 Wissen</h2>
        </div>
        <div className="knowledge-content">
          <p style={{ color: '#888' }}>Wähle erst einen Mandanten aus.</p>
        </div>
      </div>
    );
  }

  const btn = { background: 'transparent', border: '1px solid #2a2a3a', borderRadius: 6, cursor: 'pointer', padding: '4px 10px', fontSize: 12, color: '#ccc' };

  return (
    <div className="page-container">
      <div className="page-header">
        <button className="icon-btn" onClick={onBack} title="Zurück">←</button>
        <h2>📚 Wissen</h2>
        <span className="page-header-count">
          {docs.length} Dokument{docs.length === 1 ? '' : 'e'}
        </span>
      </div>

      <div className="knowledge-content">
        <div className="knowledge-upload-box">
          <p style={{ marginBottom: 12, color: '#aaa', fontSize: 13, lineHeight: 1.6 }}>
            PDF, DOCX, TXT, MD, CSV — bis 200 MB. Uploads von <strong>Admins</strong> und
            <strong> Abteilungsleitern</strong> werden sofort verarbeitet. Uploads anderer
            Mitglieder warten auf <strong style={{ color: '#f5a85a' }}>Freigabe</strong> durch
            einen Abteilungsleiter oder Admin.
          </p>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: '#888', fontWeight: 600, marginBottom: 4 }}>Wer darf dieses Dokument im Chat nutzen?</div>
            <AccessEditor
              mode={uploadAccess.mode}
              deptIds={uploadAccess.deptIds}
              userIds={uploadAccess.userIds}
              departments={departments}
              members={members}
              onChange={setUploadAccess}
            />
            {isServerAdmin && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, cursor: 'pointer', fontSize: 13, color: '#c8a2f7' }}>
                <input type="checkbox" checked={uploadHidden} onChange={e => setUploadHidden(e.target.checked)} />
                🕵️ Versteckt — nur für Server-Admins sichtbar, erscheint nie als Quelle, wird aber für Antworten genutzt.
              </label>
            )}
          </div>

          <input ref={fileInputRef} type="file" accept={ACCEPT} onChange={handleUpload} disabled={uploading} style={{ display: 'none' }} />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            style={{ padding: '10px 18px', background: 'linear-gradient(135deg, #c8f55a, #5af5c8)', color: '#0a0a0f', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
          >
            {uploading ? '… lädt hoch' : '+ Dokument hochladen'}
          </button>
        </div>

        {error && (
          <div style={{ padding: 12, background: '#3d1a2d', border: '1px solid #f55a8a', borderRadius: 8, color: '#f55a8a', marginBottom: 16, fontSize: 13 }}>
            {error}
          </div>
        )}

        {loading ? (
          <p style={{ color: '#888' }}>Lade Dokumente…</p>
        ) : docs.length === 0 ? (
          <p style={{ color: '#888' }}>Noch keine Dokumente hochgeladen.</p>
        ) : (
          <div className="knowledge-doc-list">
            {docs.map(d => {
              const badge = statusBadge(d);
              const acc = accessSummary(d);
              const isEditing = editing && editing.docId === d._id;
              const hasActions = d.canApprove || d.canViewFile || isAdmin || isServerAdmin;
              return (
                <div key={d._id} className="knowledge-doc-card">
                  <div className="knowledge-doc-body">
                    <div className="knowledge-doc-icon">📄</div>
                    <div className="knowledge-doc-info">
                      <div className="knowledge-doc-name">{d.name}</div>
                      <div className="knowledge-doc-meta">
                        {formatBytes(d.sizeBytes)} · {d.chunkCount || 0} Chunks
                      </div>
                      <AuditLines doc={d} />
                      {d.status === 'failed' && d.error && (
                        <div style={{ fontSize: 12, color: '#f55a8a', marginTop: 4 }}>Fehler: {d.error}</div>
                      )}
                    </div>
                    <div className="knowledge-doc-badges">
                      {d.hidden && (
                        <span
                          className="knowledge-doc-badge"
                          style={{ background: '#c8a2f722', color: '#c8a2f7' }}
                          title="Versteckt — nur für Server-Admins, nie als Quelle"
                        >
                          🕵️ versteckt
                        </span>
                      )}
                      <span
                        className="knowledge-doc-badge"
                        style={{ background: acc.color + '22', color: acc.color }}
                        title="Zugriffsberechtigung"
                      >
                        {acc.label}
                      </span>
                      <span
                        className="knowledge-doc-badge"
                        style={{ background: badge.color + '22', color: badge.color }}
                      >
                        {badge.label}
                      </span>
                    </div>
                  </div>

                  {hasActions && (
                    <div className="knowledge-doc-actions">
                      {d.status === 'failed' && d.canViewFile && (
                        <button onClick={() => handleRetry(d._id)} style={{ ...btn, borderColor: '#f5d55a', color: '#f5d55a' }} title="Verarbeitung erneut versuchen">↻ Erneut versuchen</button>
                      )}
                      {d.canApprove && (
                        <>
                          <button onClick={() => handleApprove(d._id)} style={{ ...btn, borderColor: '#5af5c8', color: '#5af5c8' }}>✓ Freigeben</button>
                          <button onClick={() => handleReject(d._id)} style={{ ...btn, borderColor: '#f55a8a', color: '#f55a8a' }}>✕ Ablehnen</button>
                        </>
                      )}
                      {d.canViewFile && (
                        <button onClick={() => handleViewFile(d)} style={btn}>👁 Ansehen</button>
                      )}
                      {isAdmin && (
                        <button onClick={() => isEditing ? setEditing(null) : startEditing(d)} style={isEditing ? { ...btn, borderColor: '#7aa2f7', color: '#7aa2f7' } : btn}>
                          🔑 Zugriff
                        </button>
                      )}
                      {isServerAdmin && (
                        <button
                          onClick={() => handleToggleHidden(d)}
                          style={d.hidden ? { ...btn, borderColor: '#c8a2f7', color: '#c8a2f7' } : btn}
                          title={d.hidden ? 'Wieder für alle einblenden' : 'Verstecken — nur Server-Admin, nie als Quelle'}
                        >
                          {d.hidden ? '🕵️ Eingeblendet machen' : '🕵️ Verstecken'}
                        </button>
                      )}
                      {isAdmin && (
                        <button onClick={() => handleDelete(d._id)} style={{ ...btn, color: '#f55a8a', borderColor: '#3a2030' }}>
                          🗑️ Löschen
                        </button>
                      )}
                    </div>
                  )}

                  {isEditing && (
                    <div className="knowledge-doc-editor">
                      <div style={{ fontSize: 12, color: '#888', fontWeight: 600, marginBottom: 8 }}>Zugriff für „{d.name}"</div>
                      <AccessEditor
                        mode={editing.mode}
                        deptIds={editing.deptIds}
                        userIds={editing.userIds}
                        departments={departments}
                        members={members}
                        onChange={(next) => setEditing(e => ({ ...e, ...next }))}
                      />
                      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                        <button onClick={saveAccess} disabled={savingAccess} style={{ padding: '6px 14px', background: '#5af5c8', color: '#0a0a0f', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>
                          {savingAccess ? '… speichert' : 'Speichern'}
                        </button>
                        <button onClick={() => setEditing(null)} style={{ padding: '6px 14px', background: 'transparent', color: '#aaa', border: '1px solid #2a2a3a', borderRadius: 6, cursor: 'pointer' }}>
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
